export type Experience = {
  title: string;
  company: string;
  duration: string;
  responsibilities: string[];
};

export type Education = {
  degree: string;
  institution: string;
  duration?: string;
  highlights?: string[];
};

export type CertificationCategory = {
  provider: string;
  certifications: string[];
};

export type SkillCategory = {
  category: string;
  skills: string[];
};

export type Resume = {
  summary: string;
  shortSummary: string;
  experience: Experience[];
  education: Education[];
  certifications: CertificationCategory[];
  skills: SkillCategory[];
};

export const resume: Resume = {
  summary:
    'Security architect and AI framework author. Leads security programs, incident response, and secure development practices for global organizations. Creator of @dcyfr/ai — an open-source TypeScript AI agent framework with plugin marketplace, multi-provider LLM support, delegation system, and 20+ specialist agents published on npm. Specializes in cyber risk management, vulnerability management, autonomous AI agent architectures, and operating security at scale.',
  shortSummary:
    'Security architect and AI framework author. Creator of @dcyfr/ai, an open-source TypeScript AI agent framework, and experienced leader of global security programs.',
  experience: [
    {
      title: 'Founding Architect',
      company: 'DCYFR Labs',
      duration: 'Jan 2025 → Present',
      responsibilities: [
        'Authored and maintain @dcyfr/ai — a portable open-source TypeScript AI agent framework with plugin marketplace, multi-provider LLM support (OpenAI, Anthropic, Ollama, GitHub Copilot), and delegation framework. Published on npm.',
        'Designed and shipped plugin marketplace security architecture: trust scoring engine, TLP classification, Docker sandbox isolation, automated CVE scanning, and 8 adversarial scenario mitigations.',
        'Architected delegation framework v2 with reputation engine, HMAC identity verification, SLA contracts, and TLP clearance enforcement for secure multi-agent task routing.',
        'Maintains 20+ specialist AI agents, a workspace automation platform (30 scheduled jobs), Axiom observability integration, and a proprietary context engineering knowledge system.',
      ],
    },
    {
      title: 'Founding Engineer',
      company: 'GameShark Labs LLC',
      duration: 'Apr 2026 → Present',
      responsibilities: [
        'Founding engineer (contract) on the relaunch of GameShark — a 30-year gaming brand — as a digital content platform.',
        'Lead engineering on SharkVault, the flagship web product: Next.js and Supabase, covering authentication, entitlements, subscription billing, and content access control.',
        'Established the platform security baseline — row-level security policies, automated database invariant checks, and pre-launch hardening.',
      ],
    },
    {
      title: 'Principal Security Engineer',
      company: 'Monks',
      duration: 'Jul 2023 → Present',
      responsibilities: [
        'Led audits to certify offices for ISO 27001, SOC2, TISAX, and TPN, ensuring compliance with industry standards.',
        'Implemented technical controls, reducing global vulnerabilities by 23% and enhancing security posture.',
        'Streamlined incident response processes, achieving a 35% reduction in global response times.',
      ],
    },
    {
      title: 'Security Engineering & Operations Lead',
      company: 'Monks',
      duration: 'Jul 2022 → Jul 2023',
      responsibilities: [
        'Enhanced operations and incident response programs, ensuring swift and effective response times.',
        'Developed policies and procedures for mergers and acquisitions, securing incoming integrations.',
        'Led threat hunting and vulnerability management projects to address security risks proactively.',
      ],
    },
    {
      title: 'Information Security Engineer',
      company: 'Monks',
      duration: 'Jul 2021 → Jul 2022',
      responsibilities: [
        'Developed security operations and engineering programs to enhance organizational security posture.',
        'Conducted technical audits, identifying vulnerabilities and prioritizing future security initiatives.',
        'Served as a Subject Matter Expert, providing insights and recommendations directly to the CISO.',
      ],
    },
    {
      title: 'Co-founder, VP, CTO, CISO',
      company: 'Information Security Network, Inc.',
      duration: 'Sep 2019 → Sep 2021',
      responsibilities: [
        'Served as VP on the Board of Directors, aligning IT and security objectives with nonprofit strategies.',
        'Led seminars and training events at BSides and Hack Pensacola, enhancing community engagement.',
        'Collaborated with local groups like DC850 to promote security awareness and education initiatives.',
      ],
    },
    {
      title: 'Security Operations Analyst II',
      company: 'Creative Breakthroughs, Inc.',
      duration: 'Dec 2020 → Jul 2021',
      responsibilities: [
        'Triaged and validated level one escalations, leading incident response for over 20 client contracts.',
        'Collaborated with engineering teams to resolve operational issues and enhance procedures.',
        'Developed tailored playbooks and documentation for diverse client environments.',
      ],
    },
    {
      title: 'IT Security Specialist',
      company: 'Escambia County Commissioners Office',
      duration: 'Mar 2020 → Dec 2020',
      responsibilities: [
        'Developed a comprehensive security awareness program, enhancing phishing detection by over 40%.',
        'Triaged alerts and conducted forensics and incident response for over 1k CJIS-certified systems.',
        'Applied CIS and NIST controls to bolster security for public and emergency support systems.',
      ],
    },
  ],
  education: [
    {
      degree: 'MS Information Security Engineering',
      institution: 'SANS Technology Institute',
      duration: 'Jan 2024 → Present',
      highlights: [
        'Defensible Security Architecture',
        'Hacking Techniques & Incident Response',
        'IT Security Planning, Policy, and Leadership',
        'Security Essentials',
      ],
    },
    {
      degree: 'BAS Cybersecurity',
      institution: 'Pensacola State College',
      duration: 'Dec 2020',
      highlights: [
        'Computer Science Academic Scholar',
        'Cyber Defense Club Training Officer',
        'Capture the Flag Competition Lead',
        'National Technical Honors Society',
        '3x National Cyber League Gold Bracket Finalist',
      ],
    },
  ],
  certifications: [
    {
      provider: 'GIAC',
      certifications: ['GDSA', 'GSTRT', 'GCIH', 'GSEC'],
    },
    {
      provider: 'CompTIA',
      certifications: [
        'CSIE',
        'CSAE',
        'CNVP',
        'CNSP',
        'CSAP',
        'CSIS',
        'CIOS',
        'SecurityX',
        'PenTest+',
        'CySA+',
        'Security+',
        'Network+',
        'A+',
        'ITF+',
      ],
    },
    {
      provider: 'ISC2',
      certifications: ['Candidate'],
    },
    {
      provider: 'Mile2',
      certifications: ['C)ISSO', 'C)PTE', 'C)DRE', 'C)DFE'],
    },
  ],
  skills: [
    {
      category: 'Critical Skills',
      skills: [
        'Leadership',
        'Communication',
        'Analytical Skills',
        'Collaboration',
        'Customer Service',
        'Problem Solving',
        'Critical Thinking',
        'Adaptability',
        'Time Management',
        'Project Management',
        'Mentoring & Training',
        'Documentation',
        'Risk Assessment',
        'Policy Development',
      ],
    },
    {
      category: 'Security Knowledge',
      skills: [
        'Risk Management',
        'Vulnerability Management',
        'Incident Response',
        'Threat Hunting',
        'Security Operations',
        'Cloud Security',
        'Application Security',
        'Network Security',
        'Identity & Access Management',
        'Data Protection',
        'Forensics',
        'Penetration Testing',
        'DevSecOps',
        'Compliance & Auditing',
        'Threat Analysis',
        'Security Auditing',
        'Security Awareness Training',
      ],
    },
    {
      category: 'Frameworks & Standards',
      skills: [
        'NIST',
        'CSF',
        'SOC2',
        'ISO 27001',
        'ITIL',
        'LGPD',
        'STIGs',
        'OWASP',
        'NIST 800-53',
        'CCPA/CPA',
        'CIS Controls',
        'TISAX',
        'TPN',
        'CJIS',
        'GDPR',
      ],
    },
    {
      category: 'Technologies & Tools',
      skills: [
        'Cloudflare',
        'Snyk',
        'Claude',
        'CrowdStrike',
        'Visual Studio Code',
        'Tenable',
        'Vercel',
        'Qualys',
        'ChatGPT',
        'Zscaler',
        'macOS',
        'Splunk',
        'Axiom',
        'AWS',
        'Nessus',
        'Palo Alto',
        'Azure',
        'Proofpoint',
        'Kali Linux',
        'VMware',
        'Wireshark',
        'SIEM',
        'Perplexity',
        'GitHub',
        'Carbon Black',
        'Burp Suite',
        'Unix',
        'Google Gemini',
        'Metasploit',
        'GCP',
        'Windows',
        'Google Workspace',
        'Postman',
        'Linux',
        'Microsoft Office 365',
        'Jira',
        'Slack',
        'Confluence',
        'Docker',
        'Terraform',
        'Kubernetes',
        'Ansible',
        'Jenkins',
        'Git',
        'CI/CD',
        'REST API',
      ],
    },
    {
      category: 'Programming & Automation',
      skills: [
        'Python',
        'Next.js',
        'Bash',
        'PowerShell',
        'Git',
        'Node.js',
        'TypeScript',
        'Terraform',
        'YAML',
        'Docker',
        'Kubernetes',
        'SQL',
        'Ansible',
        'React',
        'JavaScript',
        'JSON',
        'Go',
        'Java',
        'HTML/CSS',
        'C/C++',
        'PHP',
      ],
    },
  ],
};

/**
 * Calculate years of experience from formal work experience only
 * Counts from earliest job start date to present using precise date calculations
 * Does NOT include education or certifications in the year calculation
 * @param certificationDates - Optional array of certification issued dates from Credly (not used for YOE calculation)
 * @returns Approximate number of years from earliest formal work experience to present, rounded down
 */
export function getYearsOfExperience(certificationDates?: string[]): number {
  const now = new Date();
  let earliestDate: Date | null = null;

  // Parse all experience entries to find the earliest start date
  resume.experience.forEach((exp) => {
    // Extract first date from duration string (e.g., "Mar 2020" or "Sep 2019")
    const dateMatch = exp.duration.match(/^([A-Za-z]+)\s+(\d{4})/);
    if (dateMatch) {
      const monthStr = dateMatch[1];
      const year = parseInt(dateMatch[2]);

      // Month names to numbers (using first day of month as approximation)
      const months: { [key: string]: number } = {
        jan: 0,
        january: 0,
        feb: 1,
        february: 1,
        mar: 2,
        march: 2,
        apr: 3,
        april: 3,
        may: 4,
        jun: 5,
        june: 5,
        jul: 6,
        july: 6,
        aug: 7,
        august: 7,
        sep: 8,
        september: 8,
        oct: 9,
        october: 9,
        nov: 10,
        november: 10,
        dec: 11,
        december: 11,
      };

      const monthIndex = months[monthStr.toLowerCase()];
      if (monthIndex !== undefined) {
        const date = new Date(year, monthIndex, 1);
        if (!earliestDate || date < earliestDate) {
          earliestDate = date;
        }
      }
    }
  });

  // If no dates found, return 0
  if (!earliestDate) {
    return 0;
  }

  // Calculate difference in years (accounting for months and days)
  let yearsDiff = now.getFullYear() - (earliestDate as Date).getFullYear();
  const monthDiff = now.getMonth() - (earliestDate as Date).getMonth();

  // Adjust if we haven't reached the anniversary month yet this year
  if (monthDiff < 0) {
    yearsDiff--;
  }

  return yearsDiff;
}

/**
 * Get the short summary with current years of experience
 * @returns Dynamic short summary string
 */
export function getShortSummary(): string {
  const years = getYearsOfExperience();
  return `Security architect and AI framework author with ${years}+ years of experience. Creator of @dcyfr/ai — an open-source TypeScript AI agent framework published on npm.`;
}

/**
 * Get the full summary with current years of experience
 * @returns Dynamic full summary string
 */
export function getSummary(): string {
  const years = getYearsOfExperience();
  return `Security architect and AI framework author with over ${years} years of experience. Creator of @dcyfr/ai — a portable open-source TypeScript AI agent framework with plugin marketplace, delegation system, and 20+ specialist agents published on npm. Leads global security programs, incident response, and secure development practices. Specializes in cyber risk management, vulnerability management, and autonomous AI agent architectures.`;
}

export default resume;
